// Full-screen answer widgets for the Focus flow.
//
// These are the same controls the inline ReadinessCard uses, re-skinned to fill
// a step. Crucially they reuse the EXACT canonical send-text builders from
// `answerMessage.ts`, so a Focus answer is byte-identical to a chat answer and
// grounds against the same propose_card gate (self-labelled, quotable).
//
// LOOM'S ANSWER, CLOSED. When Loom has an answer for the field (`loomAnswer`,
// from `loomAnswerOf`), the control opens with NO box: Loom's text and two
// buttons. "Use this text" is the user's answer in one click — the exact bytes on
// screen, on the lane a typed answer takes. "Write my own" is the only thing that
// opens the box. See `chat/render/loomAnswer.ts`.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react";

import { isBareAssent } from "@loom/shared/readiness";

import { useMilestones } from "../dashboard/useMilestones";
import {
  buildListAnswer,
  buildMilestoneAnswer,
  defaultWidgetForField,
  listHeadingForField,
  optionLabelKey,
  PRIORITY_OPTIONS,
} from "../chat/render/answerMessage";
import {
  useArmedClick,
  useClosedOffer,
  type LoomAnswer,
} from "../chat/render/loomAnswer";
import { useT } from "../i18n/index.js";
import { reportClientEvent } from "../lib/client-trace";
import { useRemountSafeState } from "../state/remount-safe-state";
import { AutoGrowTextarea } from "../ui/AutoGrowTextarea";

import { focusDiag } from "./focus-diag";
import {
  GhostButton,
  IconCheck,
  IconPlus,
  NextButton,
  OptionButton,
} from "./ui";

const HOTKEYS = "ABCDEFGH";

/**
 * The value a CHOICE control marks as "what this would replace", and where that
 * value came from. The provenances are not interchangeable — see
 * `WidgetProps.current` for what each one means and why the sub line differs.
 */
export interface ChoiceCurrent {
  readonly value: string;
  readonly origin: "you" | "card" | "gitlab" | "github";
}

/** The one-line caption under the marked option. One function, so the two
 *  choice controls cannot label the same provenance differently. */
const currentSubLabel = (
  t: ReturnType<typeof useT>,
  origin: ChoiceCurrent["origin"],
): string =>
  origin === "you"
    ? t("focus.refine.choice.yourPick")
    : origin === "gitlab" || origin === "github"
      ? t("focus.refine.choice.onIssueNow")
      : t("focus.refine.choice.onCardNow");

/** How a click on Loom's answer is told apart from typing — for the undo path
 *  only (an undone click returns the step CLOSED). Never reaches the wire: the
 *  server cannot tell a click from typing, and is not told. */
export interface AnswerMeta {
  readonly via: "loom";
}

/**
 * What a bare "yes" typed into this control does.
 *
 * `"refuse"` — an OPEN value field (role, means, ends, a list): a yes names
 * nothing, so it is not sent, and a hint says why next to Loom's answer.
 * `"keep"` — an editor over a value that already stands (the ready editor, the
 * review "Change"): a yes is a keep, sends nothing, and `onKeep` closes it.
 *
 * Omitted on everything else — a field-less question, a concern reply, a note:
 * there "ja" IS the answer.
 */
export type AssentGuard = "refuse" | "keep";

interface WidgetProps {
  readonly field: string;
  readonly projectId?: number;
  readonly onAnswer: (text: string, meta?: AnswerMeta) => void;
  readonly placeholder?: string;
  /** Accessible name for the input(s), since the question is a separate <h1>. */
  readonly ariaLabel?: string;
  /**
   * Store key for the in-progress draft (RefineFlow provides the store).
   * The widget subtree is unmounted by BOTH remount channels — the
   * reconnect timeline wipe and FocusFrame's `key={stepKey}` fade-up
   * remount — so the half-typed value must live above the widget or it
   * dies with the subtree. Site-prefixed per mount site (`q:`, `review:`,
   * `edit:`, `correct`) so a question draft never bleeds into the
   * review editor for the same field. Omitted ⇒ plain local state.
   */
  readonly draftKey?: string;
  /**
   * Pre-fill for a control mounted over a field that ALREADY has a value —
   * an edit/review re-open, or an undo. Both undefined by default, so a
   * genuinely fresh question (nothing to seed) is unaffected. Only consulted
   * on FIRST mount: a stored draft (see `draftKey`) shadows the seed, so a
   * half-typed value the user came back to always wins. `initialValue` seeds
   * the text box; `initialRows` seeds a list_builder's committed rows. These
   * are the USER's own bytes (a held answer, an undo, the value being edited) —
   * Loom's bytes never arrive here, they arrive as `loomAnswer`.
   */
  readonly initialValue?: string;
  readonly initialRows?: readonly string[];
  /**
   * Loom's answer for this field (`loomAnswerOf`), shown CLOSED: its text, "Use
   * this text" and "Write my own", and no box.
   *
   * "Use this text" sends exactly the bytes on screen, on the lane a typed
   * answer takes — it is the user's own act, and the gate grounds the echo the
   * way it grounds typing. That is why these bytes may never ride `initialValue`:
   * a pre-filled box is indistinguishable from the user's own draft, so a stray
   * ⌘Enter would spend Loom's sentence with no decision behind it. Loom may
   * OFFER bytes; only the user may spend them — and the click is the user
   * spending them.
   *
   * A CHOICE answer marks its option "Loom's suggestion" instead; one click on
   * the option picks it.
   */
  readonly loomAnswer?: LoomAnswer;
  /** See `AssentGuard`. */
  readonly assentGuard?: AssentGuard;
  /** The keep path of `assentGuard: "keep"`. */
  readonly onKeep?: () => void;
  /** OK is dead while the box still holds `initialValue` (the rows
   *  `initialRows`) unchanged — an editor opened "to check" is not an edit. */
  readonly refuseUnchanged?: boolean;
  /** The field's label, carried into the buttons' accessible names ("Use this
   *  text (Outcome)"), so a screen with several rows has distinct names. */
  readonly answerLabel?: string;
  /** Content-free `ui.click` surface token for the closed controls. */
  readonly traceSurface?: string;
  /** Told whenever Loom's answer goes closed or open, so the host can fold its
   *  composer into a note link while no box is open. */
  readonly onClosedChange?: (closed: boolean) => void;
  /**
   * Whether the box takes the caret when it opens (default true: a Focus step
   * shows one question). A page that lists several controls at once (the
   * meeting page's open questions) passes false, or the last one would take
   * the caret and scroll the page to it.
   */
  readonly autoFocus?: boolean;
  /**
   * The value this CHOICE field already carries, so a select control can show
   * which option the answer would replace. Two provenances, and they are not
   * interchangeable:
   *
   * `"you"` — the user's own previous pick, still held locally (the undo path).
   * Marking it is the whole point of an undo: "Undo — go back" used to return to
   * the milestone question with twenty identical options and nothing to say what
   * was being undone.
   *
   * `"card"` — the value the readiness card holds for the field. It is what the
   * agent's own question means by "keep that", and the copy says exactly that
   * much (what Loom has now) rather than claiming it is the issue's value: the
   * card's slot can hold either a reading of the issue or a proposal, and this
   * surface cannot tell them apart.
   *
   * `"gitlab"` / `"github"` — what the issue carries right now, read from the
   * fetched issue rather than from the card. This is the one an OPEN question
   * has: a `needs_input` slot carries no `value` at all (the agent's ask sends
   * only a question), so on the very step where the agent says "it's currently
   * flagged as Priority 2 — keep that, or change it?" both provenances above
   * are empty and nothing was marked. Never guessed: the caller passes it only
   * when the issue actually carries the label (see `issueChoiceFor`). Both
   * forges read the same caption, which names no forge.
   *
   * Ignored by every non-choice control — a text box shows its value in the box.
   */
  readonly current?: ChoiceCurrent;
}

/** Report a closed-answer control, content-free: a control token and
 *  a surface token, never the text. */
const traceClick = (control: string, surface: string | undefined): void => {
  if (surface === undefined) return;
  reportClientEvent("ui.click", { control, surface });
};

/** Is focus sitting in something that eats keystrokes? */
const isTextEntry = (el: Element | null): boolean => {
  if (el === null) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    (el as HTMLElement).isContentEditable === true
  );
};

/**
 * Whether the A/B/C hotkeys are LIVE right now.
 *
 * They are not, while the composer (or any other text box) holds focus: the
 * keystroke belongs to whatever the user is typing. That has always been true —
 * `useHotkeySelect` ignores those events — but the badges kept claiming
 * otherwise, so after "Send as a note" pressing `b` typed a "b" into the message
 * box instead of picking option B, silently. And that box's primary Send is the
 * ATTRIBUTED answer for the pinned question, so the stray character became a
 * candidate answer.
 *
 * The badge is therefore rendered from this hook: hotkeys work, or the
 * affordance is not on screen. `focusout` fires BEFORE focus moves, so the read
 * is deferred a tick — otherwise leaving the composer for the option list would
 * still measure the composer.
 */
function useHotkeysArmed(): boolean {
  const [armed, setArmed] = useState(
    () => typeof document === "undefined" || !isTextEntry(document.activeElement),
  );
  useEffect(() => {
    let timer: number | null = null;
    const sync = (): void => setArmed(!isTextEntry(document.activeElement));
    const later = (): void => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        sync();
      }, 0);
    };
    sync();
    document.addEventListener("focusin", sync);
    document.addEventListener("focusout", later);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      document.removeEventListener("focusin", sync);
      document.removeEventListener("focusout", later);
    };
  }, []);
  return armed;
}

/**
 * Bind A–H to the first options so the visible hotkey badges actually work.
 * Ignores keystrokes while a text field is focused (the message bar, list
 * draft) or any modifier is held; capped at HOTKEYS.length so every bound
 * option has a badge.
 */
function useHotkeySelect(count: number, onPick: (i: number) => void): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTextEntry(e.target as Element | null)) return;
      if (e.key.length !== 1) return;
      const i = HOTKEYS.indexOf(e.key.toUpperCase());
      if (i >= 0 && i < count) {
        e.preventDefault();
        onPick(i);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, onPick]);
}

/** Case/whitespace-insensitive match of a stored value against an option. A
 *  choice value travels self-labelled ("Priority: High", "Milestone: v2"), but
 *  `prettyValue` strips that label for a text seed — so both shapes have to
 *  resolve to the same option or an undo shows nothing selected. */
const sameChoice = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/* ─── Loom's answer, closed ──────────────────────────────────────────── */

/**
 * The block Loom's answer sits in: its label, then its text or rows, then (when
 * closed) the two buttons. `role="group"`, labelled by the label line, so a
 * screen reader hears whose words these are before it hears the buttons.
 *
 * `reference` is the same block while the user writes their own: Loom's text
 * stays above the box as something to read, not something to press.
 */
export function LoomBlock({
  text,
  rows,
  reference = false,
  wording = false,
  children,
}: {
  readonly text?: string;
  readonly rows?: ReadonlyArray<string>;
  readonly reference?: boolean;
  /** A settled red's value, not a draft: labelled "Loom's wording", the same
   *  words the step's kicker uses, so the two cannot disagree (#257, en-12). */
  readonly wording?: boolean;
  readonly children?: ReactNode;
}) {
  const t = useT();
  const labelId = useId();
  return (
    <div
      className={`focus-suggest${reference ? " is-reference" : " is-closed"}`}
      role="group"
      aria-labelledby={labelId}
    >
      <p id={labelId} className="focus-suggest-label">
        {t(wording ? "focus.refine.suggest.fromLoomWording" : "focus.refine.suggest.fromLoom")}
      </p>
      {rows !== undefined ? (
        <ol className="focus-suggest-rows">
          {rows.map((r, i) => (
            <li key={`${i}-${r}`}>{r}</li>
          ))}
        </ol>
      ) : (
        <div className="focus-suggest-text">{text}</div>
      )}
      {children}
    </div>
  );
}

/**
 * The accept button. Never autofocused — a reflexive Enter on a new step must not
 * spend Loom's words — and armed against a carry-over click (`useArmedClick`).
 */
export function UseButton({
  label,
  ariaLabel,
  onUse,
  buttonRef,
}: {
  readonly label: string;
  readonly ariaLabel?: string;
  readonly onUse: () => void;
  readonly buttonRef?: Ref<HTMLButtonElement>;
}) {
  const armed = useArmedClick();
  return (
    <button
      ref={buttonRef}
      type="button"
      className="focus-btn is-ok focus-suggest-use"
      {...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {})}
      onClick={(e) => {
        if (!armed(e)) return;
        onUse();
      }}
    >
      {label} <IconCheck />
    </button>
  );
}

/** The bare-yes hint, next to Loom's answer when there is one. */
function AssentHint({
  message,
  children,
}: {
  readonly message: string;
  readonly children?: ReactNode;
}) {
  return (
    <div className="focus-assent-hint" role="status">
      <span>{message}</span>
      {children}
    </div>
  );
}

/* ─── Free text (role / means / ends) ────────────────────────────────── */

function TextWidget({
  field,
  onAnswer,
  placeholder,
  ariaLabel,
  draftKey,
  initialValue,
  loomText,
  assentGuard,
  onKeep,
  refuseUnchanged = false,
  answerLabel,
  traceSurface,
  onClosedChange,
  loomWording = false,
  autoFocus = true,
}: {
  readonly field?: string;
  readonly onAnswer: (text: string, meta?: AnswerMeta) => void;
  readonly placeholder?: string;
  readonly ariaLabel?: string;
  readonly draftKey?: string;
  readonly initialValue?: string;
  readonly loomText?: string;
  readonly assentGuard?: AssentGuard;
  readonly onKeep?: () => void;
  readonly refuseUnchanged?: boolean;
  readonly answerLabel?: string;
  readonly traceSurface?: string;
  readonly onClosedChange?: (closed: boolean) => void;
  readonly loomWording?: boolean;
  readonly autoFocus?: boolean;
}) {
  const t = useT();
  const [value, setValue] = useRemountSafeState(draftKey ?? null, initialValue ?? "");
  const offer = loomText !== undefined && loomText.trim() !== "" ? loomText.trim() : null;
  const { closed, openOwn, backToLoom, onBoxFocus, onBoxBlur, onBoxInteract, closedOnCaret } =
    useClosedOffer({
      draftKey: draftKey ?? null,
      offerKey: offer,
      hasText: value.trim() !== "",
    });
  // The bare-yes hint, raised by a refused submit and retired by the next edit.
  const [assentShown, setAssentShown] = useState(false);
  // After "Back", focus returns to the accept button it came from.
  const acceptRef = useRef<HTMLButtonElement | null>(null);
  const focusAcceptRef = useRef(false);
  useEffect(() => {
    if (closed && focusAcceptRef.current) {
      focusAcceptRef.current = false;
      acceptRef.current?.focus();
    }
  }, [closed]);
  // A draft that arrived while the page's own caret sat in the empty box closes
  // the step; the caret goes to "Write my own" — the one control there whose
  // Enter spends nothing — rather than to `body`.
  const ownRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (closedOnCaret) ownRef.current?.focus();
  }, [closedOnCaret]);
  // "Start from Loom's text" puts the text in the box AND the caret at its end,
  // so the one-word tweak it exists for is one keystroke away. Without it the
  // link unmounted under the caret and typing landed nowhere (#251).
  const boxRef = useRef<HTMLTextAreaElement | null>(null);
  const caretToEndRef = useRef(false);
  useEffect(() => {
    if (!caretToEndRef.current) return;
    caretToEndRef.current = false;
    const el = boxRef.current;
    if (el === null) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    onBoxInteract();
  }, [value, onBoxInteract]);
  useEffect(() => {
    onClosedChange?.(closed);
  }, [closed, onClosedChange]);
  const unchanged =
    refuseUnchanged && value.trim() === (initialValue ?? "").trim();
  const use = (): void => {
    if (offer === null) return;
    focusDiag("suggestion_adopted", { field: field ?? "answer" });
    traceClick("loom_answer_use", traceSurface);
    // The string rendered in THIS render — never a newer draft that landed
    // between the render and the click.
    onAnswer(offer, { via: "loom" });
  };
  const back = (): void => {
    traceClick("loom_answer_back", traceSurface);
    setValue("");
    setAssentShown(false);
    focusAcceptRef.current = true;
    backToLoom();
  };
  const submit = () => {
    // NB `trimmed`, not `t`: `t` is the translator in this scope now.
    const trimmed = value.trim();
    if (trimmed === "" || unchanged) return;
    if (assentGuard !== undefined && isBareAssent(trimmed)) {
      if (assentGuard === "keep") {
        // A yes over a value that already stands keeps it. The box goes back to
        // that value, so a later re-open does not come back holding "yes".
        setValue(initialValue ?? "");
        onKeep?.();
        return;
      }
      setAssentShown(true);
      return;
    }
    onAnswer(trimmed);
    setValue("");
  };
  if (closed && offer !== null) {
    return (
      <LoomBlock text={offer} wording={loomWording}>
        <div className="focus-suggest-actions">
          <UseButton
            buttonRef={acceptRef}
            label={t("focus.refine.suggest.adopt")}
            {...(answerLabel !== undefined
              ? { ariaLabel: t("focus.refine.suggest.adoptAria", { label: answerLabel }) }
              : {})}
            onUse={use}
          />
          <button
            ref={ownRef}
            type="button"
            className="focus-btn is-ghost focus-suggest-own"
            {...(answerLabel !== undefined
              ? { "aria-label": t("focus.refine.suggest.writeOwnAria", { label: answerLabel }) }
              : {})}
            onClick={() => {
              traceClick("loom_answer_own", traceSurface);
              openOwn();
            }}
          >
            {t("focus.refine.suggest.writeOwn")}
          </button>
        </div>
      </LoomBlock>
    );
  }
  const boxEmpty = value.trim() === "";
  const showBack = offer !== null && (boxEmpty || value.trim() === offer);
  return (
    <>
      {offer !== null && <LoomBlock text={offer} reference wording={loomWording} />}
      <AutoGrowTextarea
        // Every free-text step in the flow gets the same clearly-multiline
        // bordered box — a plain underline field with a 1-line bump still
        // read as a single-line input at rest (the field-less agent_ask
        // steps hit this: they never got the "ends"/"business_correction"
        // multiline special-case, so they stayed on the underline style).
        ref={boxRef}
        className="focus-field is-box"
        autoFocus={autoFocus}
        minLines={3}
        aria-label={ariaLabel ?? t("focus.widget.text.aria")}
        placeholder={placeholder ?? t("focus.widget.text.placeholder")}
        value={value}
        onFocus={onBoxFocus}
        onBlur={onBoxBlur}
        onPointerDown={onBoxInteract}
        onKeyUp={onBoxInteract}
        onChange={(e) => {
          setValue(e.target.value);
          setAssentShown(false);
        }}
        onKeyDown={(e) => {
          onBoxInteract();
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
          // Esc in an EMPTY box is "Back": nothing typed is thrown away.
          if (e.key === "Escape" && offer !== null && value.trim() === "") {
            e.preventDefault();
            back();
          }
        }}
      />
      {showBack && (
        <div className="focus-suggest-links">
          {boxEmpty && (
            <button
              type="button"
              className="focus-link"
              onClick={() => {
                traceClick("loom_answer_start", traceSurface);
                caretToEndRef.current = true;
                setValue(offer);
              }}
            >
              {t("focus.refine.suggest.startFrom")}
            </button>
          )}
          <button type="button" className="focus-link" onClick={back}>
            {t("focus.refine.suggest.back")}
          </button>
        </div>
      )}
      {assentShown && (
        <AssentHint
          message={
            offer !== null
              ? t("focus.refine.suggest.assentHint")
              : t("focus.refine.suggest.assentHintNoDraft")
          }
        >
          {offer !== null && (
            <UseButton
              label={t("focus.refine.suggest.adopt")}
              {...(answerLabel !== undefined
                ? { ariaLabel: t("focus.refine.suggest.adoptAria", { label: answerLabel }) }
                : {})}
              onUse={use}
            />
          )}
        </AssentHint>
      )}
      <NextButton onClick={submit} disabled={boxEmpty || unchanged} />
    </>
  );
}

/* ─── List builder (acceptance criteria / DoD) ───────────────────────── */

/**
 * The rows AS THEY ARE ON SCREEN — i.e. with a row editor that is still open
 * folded in.
 *
 * The list's primary "Done" sits directly under the open editor, one button
 * away from the editor's own Save, and it used to send the PRE-edit `rows` and
 * then clear the editor: the typo the user was looking at is what grounded and
 * what got written to the issue, with nothing on screen to say the correction
 * had been dropped. What is submitted has to be what is rendered.
 *
 * An emptied box keeps the pre-edit row, which is exactly `commitEdit`'s rule:
 * clearing the box is not a delete (the × is one click away and says so). Same
 * function on both paths so the two can't drift.
 */
function withPendingEdit(
  rows: ReadonlyArray<string>,
  editIndex: number | null,
  editText: string,
): ReadonlyArray<string> {
  if (editIndex === null) return rows;
  const next = editText.trim();
  if (next === "") return rows;
  return rows.map((r, j) => (j === editIndex ? next : r));
}

/**
 * Everything VISIBLE in the list control, as the message it would send.
 *
 * There are TWO in-progress inputs on this control, not one: the row editor
 * (folded in by `withPendingEdit`) and the "Add an item…" draft. Done used to
 * fold the first and silently drop the second — a criterion the user had typed
 * but not pressed Add on vanished on submit, and the box was cleared behind it,
 * which is the same "your text disappeared" defect the row editor had.
 *
 * The draft lands where Add would have put it: at the END of the list. Trimmed
 * and skipped when blank, exactly like `addRow`, so a stray space is not a row.
 */
function visibleRows(
  rows: ReadonlyArray<string>,
  editIndex: number | null,
  editText: string,
  draft: string,
): ReadonlyArray<string> {
  const withEdit = withPendingEdit(rows, editIndex, editText);
  const pending = draft.trim();
  return pending === "" ? withEdit : [...withEdit, pending];
}

const sameRows = (a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean =>
  a.length === b.length && a.every((r, i) => r === b[i]);

function ListWidget({
  field,
  onAnswer,
  placeholder,
  ariaLabel,
  draftKey,
  initialRows,
  loomRows,
  assentGuard,
  onKeep,
  refuseUnchanged = false,
  answerLabel,
  traceSurface,
  onClosedChange,
  loomWording = false,
  autoFocus = true,
}: {
  readonly field: string;
  readonly onAnswer: (text: string, meta?: AnswerMeta) => void;
  readonly placeholder?: string;
  readonly ariaLabel?: string;
  readonly draftKey?: string;
  readonly initialRows?: readonly string[];
  readonly loomRows?: ReadonlyArray<string>;
  readonly assentGuard?: AssentGuard;
  readonly onKeep?: () => void;
  readonly refuseUnchanged?: boolean;
  readonly answerLabel?: string;
  readonly traceSurface?: string;
  readonly onClosedChange?: (closed: boolean) => void;
  readonly loomWording?: boolean;
  readonly autoFocus?: boolean;
}) {
  const t = useT();
  const heading = listHeadingForField(field);
  const offerRows = (loomRows ?? []).map((r) => r.trim()).filter((r) => r !== "");
  // The offer's identity is the message it would SEND — the same bytes "Use this
  // list" commits.
  const offer = offerRows.length > 0 ? buildListAnswer(heading, offerRows) : null;
  // Two pieces survive a remount: the COMMITTED rows (the larger loss — several
  // typed criteria) and the in-progress draft row. The committed rows seed from
  // `initialRows` when the control opens over an existing value (the user's own
  // bytes); Loom's rows are never a seed — they arrive through "Change the list".
  const [rows, setRows] = useRemountSafeState<ReadonlyArray<string>>(
    draftKey !== undefined ? `${draftKey}:rows` : null,
    initialRows ?? [],
  );
  // Whether the rows on screen STARTED as Loom's list — drives the caption. It
  // stays true after the user edits a row, because it is a statement about where
  // the list came from, and that does not stop being true once they have
  // corrected part of it.
  const [fromLoom, setFromLoom] = useRemountSafeState(
    draftKey !== undefined ? `${draftKey}:fromLoom` : null,
    false,
  );
  const [draft, setDraft] = useRemountSafeState(
    draftKey !== undefined ? `${draftKey}:draft` : null,
    "",
  );
  // In-place row editing. Without it the only way to fix a typo was to remove
  // the row and re-add it — which appends, so the list silently reorders and an
  // acceptance criterion the user wrote first ends up last. Both halves are
  // remount-safe for the same reason the rows are: FocusFrame remounts this
  // subtree on every step key change, and a half-typed correction must not die
  // with it.
  const [editIndex, setEditIndex] = useRemountSafeState<number | null>(
    draftKey !== undefined ? `${draftKey}:editIndex` : null,
    null,
  );
  const [editText, setEditText] = useRemountSafeState(
    draftKey !== undefined ? `${draftKey}:editText` : null,
    "",
  );
  const { closed, openOwn, backToLoom, onBoxFocus, onBoxBlur, onBoxInteract, closedOnCaret } =
    useClosedOffer({
      draftKey: draftKey ?? null,
      offerKey: offer,
      hasText: rows.length > 0 || draft.trim() !== "" || editIndex !== null,
    });
  useEffect(() => {
    onClosedChange?.(closed);
  }, [closed, onClosedChange]);
  // Same hand-off as the text box: a list that closed on the page's own caret
  // gives it to "Change the list", never to `body`.
  const changeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (closedOnCaret) changeRef.current?.focus();
  }, [closedOnCaret]);
  // "A yes isn't a criterion": raised by a refused Add or Done, retired by the
  // next edit of the draft row.
  const [assentRow, setAssentRow] = useState(false);
  const beginEdit = (i: number, value: string): void => {
    setEditIndex(i);
    setEditText(value);
  };
  const cancelEdit = (): void => {
    setEditIndex(null);
    setEditText("");
  };
  const commitEdit = (): void => {
    const at = editIndex;
    // An emptied row is a REMOVE-by-another-name and it is not offered here: the
    // × is one click away and says so. Silently dropping the row on Save would
    // make "clear the box" mean delete. (`withPendingEdit` holds that rule.)
    if (at === null || editText.trim() === "") return;
    if (assentGuard !== undefined && isBareAssent(editText.trim())) {
      setAssentRow(true);
      return;
    }
    setRows((cur) => withPendingEdit(cur, at, editText));
    setEditIndex(null);
    setEditText("");
  };
  // The caret must stay in the draft box across an add, so a list can be built
  // without reaching for the mouse twice. The Add button is disabled the moment
  // `draft` clears, and browsers BLUR a focused element that becomes disabled —
  // focus then lands on `document.body` and every subsequent keystroke goes
  // nowhere. Nothing in this app restored it.
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  const addRow = () => {
    // `trimmed`, not `t` — `t` is this component's translator.
    const trimmed = draft.trim();
    if (trimmed === "") return;
    // A yes names nothing, and as a ROW it would be written into the issue as a
    // criterion. Refused on every guarded list.
    if (assentGuard !== undefined && isBareAssent(trimmed)) {
      setAssentRow(true);
      draftRef.current?.focus();
      return;
    }
    setRows((r) => [...r, trimmed]);
    setDraft("");
    // Synchronous, inside the handler: focus moves before React commits the
    // disabled state, so there is nothing for the browser to blur away from.
    draftRef.current?.focus();
  };
  const onScreen = visibleRows(rows, editIndex, editText, draft);
  const unchanged = refuseUnchanged && sameRows(onScreen, initialRows ?? []);
  const submit = () => {
    // The list on screen, not the list behind the open editor and not the list
    // without the row the user is still typing — see `visibleRows`. Done is the
    // primary action; it commits what the user is looking at rather than
    // discarding it on the way out.
    const sent = onScreen;
    if (sent.length === 0 || unchanged) return;
    if (assentGuard !== undefined) {
      const yeses = sent.filter((r) => isBareAssent(r)).length;
      if (assentGuard === "keep" && yeses === sent.length) {
        // A list of nothing but yeses over rows that already stand is a keep.
        setRows(initialRows ?? []);
        setDraft("");
        cancelEdit();
        onKeep?.();
        return;
      }
      if (yeses > 0) {
        setAssentRow(true);
        return;
      }
    }
    onAnswer(buildListAnswer(heading, sent));
    setRows([]);
    setFromLoom(false);
    // Clear the in-progress row too: drafts are remount-safe now, so a
    // leftover half-typed row would otherwise pre-fill a LATER re-ask of this
    // field (pre-persistence it silently died with the unmount).
    setDraft("");
    cancelEdit();
  };
  const takeLoomRows = (): void => {
    setRows(offerRows);
    setFromLoom(true);
    openOwn();
  };
  const back = (): void => {
    traceClick("loom_answer_back", traceSurface);
    setRows([]);
    setDraft("");
    cancelEdit();
    setFromLoom(false);
    setAssentRow(false);
    backToLoom();
  };
  if (closed && offer !== null) {
    return (
      <LoomBlock rows={offerRows} wording={loomWording}>
        <div className="focus-suggest-actions">
          <UseButton
            label={t("focus.refine.suggest.adoptList")}
            {...(answerLabel !== undefined
              ? { ariaLabel: t("focus.refine.suggest.adoptListAria", { label: answerLabel }) }
              : {})}
            onUse={() => {
              focusDiag("suggestion_adopted", { field });
              traceClick("loom_answer_use", traceSurface);
              onAnswer(offer, { via: "loom" });
            }}
          />
          <button
            ref={changeRef}
            type="button"
            className="focus-btn is-ghost focus-suggest-own"
            {...(answerLabel !== undefined
              ? { "aria-label": t("focus.refine.suggest.changeListAria", { label: answerLabel }) }
              : {})}
            onClick={() => {
              traceClick("loom_answer_change", traceSurface);
              takeLoomRows();
            }}
          >
            {t("focus.refine.suggest.changeList")}
          </button>
        </div>
      </LoomBlock>
    );
  }
  // "Back" is offered while nothing on screen is the user's own work: the rows
  // are still exactly Loom's (or empty) and nothing is being typed.
  const untouched =
    offer !== null &&
    draft.trim() === "" &&
    editIndex === null &&
    (rows.length === 0 || sameRows(rows, offerRows));
  return (
    <div className="focus-list">
      {/* Loom's list above an EMPTY builder is a reference (an offer that
          arrived while the user was already writing); once it is IN the list,
          the caption below says so instead. */}
      {offer !== null && rows.length === 0 && (
        <LoomBlock rows={offerRows} reference wording={loomWording} />
      )}
      {fromLoom && rows.length > 0 && (
        <p className="focus-suggest-label">{t("focus.refine.suggest.prefilled")}</p>
      )}
      {rows.length > 0 && (
        <div className="focus-list-rows">
          {rows.map((r, i) => (
            <div key={`${i}-${r}`} className="focus-list-row">
              <span className="focus-list-bullet">{i + 1}</span>
              {editIndex === i ? (
                <>
                  <AutoGrowTextarea
                    className="focus-field focus-list-edit"
                    autoFocus
                    aria-label={t("focus.refine.list.editRow", { n: String(i + 1) })}
                    value={editText}
                    onChange={(e) => {
                      setEditText(e.target.value);
                      setAssentRow(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        commitEdit();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        cancelEdit();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="focus-list-save"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={commitEdit}
                    disabled={editText.trim() === ""}
                  >
                    {t("common.save")}
                  </button>
                  <button
                    type="button"
                    className="focus-list-remove"
                    aria-label={t("common.cancel")}
                    onClick={cancelEdit}
                  >
                    ×
                  </button>
                </>
              ) : (
                <>
                  <span className="focus-list-text">{r}</span>
                  {/* Edits the row WHERE IT IS. Remove-and-re-add appends, so a
                      typo fix used to move the criterion to the end of the
                      list — and the order of acceptance criteria is the order
                      the issue is written in. */}
                  <button
                    type="button"
                    className="focus-list-edit-btn"
                    aria-label={t("focus.refine.list.editRow", { n: String(i + 1) })}
                    onClick={() => beginEdit(i, r)}
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    type="button"
                    className="focus-list-remove"
                    aria-label={t("focus.refine.list.removeRow", { n: String(i + 1) })}
                    onClick={() => {
                      setRows((cur) => cur.filter((_, j) => j !== i));
                      // Only the deleted row's OWN edit is discarded. A
                      // different row's in-progress correction must survive —
                      // shift its index down instead of cancelling it, same as
                      // the removed row itself would if it were the one being
                      // edited.
                      if (editIndex === i) cancelEdit();
                      else if (editIndex !== null && editIndex > i) {
                        setEditIndex(editIndex - 1);
                      }
                    }}
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="focus-list-add">
        <AutoGrowTextarea
          ref={draftRef}
          className="focus-field"
          // Focus lands in the add box when the builder opens on its own (no
          // Loom answer, or the user just chose "Change the list"). A builder
          // opened pre-filled still takes the caret here, where the next row goes.
          autoFocus={autoFocus}
          aria-label={ariaLabel ?? t("focus.widget.list.addAria")}
          placeholder={placeholder ?? t("focus.widget.list.addPlaceholder")}
          value={draft}
          onFocus={onBoxFocus}
          onBlur={onBoxBlur}
          onPointerDown={onBoxInteract}
          onKeyUp={onBoxInteract}
          onChange={(e) => {
            setDraft(e.target.value);
            setAssentRow(false);
          }}
          onKeyDown={(e) => {
            onBoxInteract();
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              addRow();
            }
          }}
        />
        <button
          type="button"
          className="focus-btn is-ghost"
          // Never takes focus in the first place, so there is nothing to blur
          // when `addRow` clears the draft and disables it. The belt is the
          // explicit re-focus in `addRow`; this is the structural half.
          onMouseDown={(e) => e.preventDefault()}
          onClick={addRow}
          disabled={draft.trim() === ""}
        >
          <IconPlus /> {t("common.add")}
        </button>
      </div>
      {assentRow && <AssentHint message={t("focus.refine.suggest.assentRow")} />}
      {untouched && (
        <div className="focus-suggest-links">
          {rows.length === 0 && (
            <button
              type="button"
              className="focus-link"
              onClick={() => {
                traceClick("loom_answer_start", traceSurface);
                takeLoomRows();
                // The link unmounts with this click (the rows are no longer
                // empty): the caret goes to the add box, where the next row goes.
                draftRef.current?.focus();
              }}
            >
              {t("focus.refine.suggest.startFrom")}
            </button>
          )}
          <button type="button" className="focus-link" onClick={back}>
            {t("focus.refine.suggest.back")}
          </button>
        </div>
      )}
      {/* ⌘/Ctrl+Enter in the draft ADDS a row; it doesn't submit the whole list
          (that's the Done button below) — so no Enter hint here. */}
      <NextButton
        onClick={submit}
        // Armed by anything the submit would SEND, which now includes the
        // un-added draft row: a control showing one typed criterion under a
        // dead Done button is the same "what is rendered is what is sent" break
        // from the other side.
        disabled={onScreen.length === 0 || unchanged}
        label={t("focus.widget.list.done")}
        showEnterHint={false}
      />
    </div>
  );
}

/* ─── Single select (priority) ───────────────────────────────────────── */

/**
 * Does `option` name the same choice as Loom's suggestion? A draft reads "High",
 * "Priority: High" or the localized label, and each of those must find the same
 * option — matching the contract value alone would almost never mark one.
 */
const namesOption = (
  suggestion: string,
  option: { readonly value: string; readonly label: string },
  shownLabel: string,
): boolean =>
  sameChoice(option.value, suggestion) ||
  sameChoice(option.label, suggestion) ||
  sameChoice(shownLabel, suggestion) ||
  sameChoice(option.value.replace(/^[^:]+:\s*/u, ""), suggestion.replace(/^[^:]+:\s*/u, ""));

function SelectWidget({
  options,
  onAnswer,
  ariaLabel,
  current,
  suggestedOption,
}: {
  readonly options: ReadonlyArray<{ value: string; label: string }>;
  readonly onAnswer: (text: string) => void;
  readonly ariaLabel?: string;
  readonly current?: ChoiceCurrent;
  /** The option Loom's answer names — marked "Loom's suggestion". */
  readonly suggestedOption?: string;
}) {
  const t = useT();
  const armed = useHotkeysArmed();
  const pick = useCallback(
    (i: number) => {
      const o = options[i];
      if (o) onAnswer(o.value);
    },
    [options, onAnswer],
  );
  useHotkeySelect(armed ? Math.min(options.length, HOTKEYS.length) : 0, pick);
  // The option's `value` is the grounding plane and stays English; its LABEL is
  // chrome and follows the UI locale. Live, "High" / "Normal" / "Low" sat under
  // a German question in an otherwise fully German session — the `priority.*`
  // keys already existed, nothing routed the widget through them. An option this
  // map does not own (an agent-supplied one) keeps its own text.
  const labelOf = (o: { value: string; label: string }): string => {
    const key = optionLabelKey(o.value);
    return key === null ? o.label : t(key);
  };
  const selectedIndex =
    current === undefined
      ? -1
      : options.findIndex(
          (o) => sameChoice(o.value, current.value) || sameChoice(o.label, current.value),
        );
  const suggestedIndex =
    suggestedOption === undefined
      ? -1
      : options.findIndex((o) => namesOption(suggestedOption, o, labelOf(o)));
  // The current value's line wins when both marks name one option: that option
  // is already what the issue has, and the one line has to say so.
  const subOf = (i: number): string | null =>
    i === selectedIndex && current !== undefined
      ? currentSubLabel(t, current.origin)
      : i === suggestedIndex
        ? t("focus.refine.choice.loomSuggests")
        : null;
  return (
    <>
      <div
        className="focus-options"
        role="radiogroup"
        aria-label={ariaLabel ?? t("focus.refine.choice.groupAria")}
      >
        {options.map((o, i) => (
          <OptionButton
            key={o.value}
            label={labelOf(o)}
            selected={i === selectedIndex}
            {...(subOf(i) !== null ? { sub: subOf(i)! } : {})}
            {...(armed && HOTKEYS[i] ? { hotkey: HOTKEYS[i] } : {})}
            onClick={() => onAnswer(o.value)}
          />
        ))}
      </div>
      {/* The option set is CLOSED, and the agent's question does not know that:
          live it said "It's currently flagged as Priority 2 in GitLab — keep
          that, or change it?" over three buttons reading High / Normal / Low,
          none of them "Priority 2" and none of them marked. This says what the
          buttons can actually do, under the buttons, in every language the UI
          speaks.

          OUTSIDE the radiogroup: a `radiogroup` owns radios, and a paragraph
          among them is markup a screen reader has to guess at. */}
      <p className="focus-help focus-options-note">
        {/* With the issue's CURRENT value marked, one of these buttons IS the
            "keep that" the agent's question offers — say which, or the marked
            row is decoration. */}
        {(current?.origin === "gitlab" || current?.origin === "github") &&
        selectedIndex >= 0
          ? t("focus.refine.choice.onlyOptionsKeep")
          : t("focus.refine.choice.onlyOptions")}
      </p>
    </>
  );
}

/* ─── Milestone (live options) ───────────────────────────────────────── */

/**
 * Above this many milestones the list stops being a set of choices and becomes a
 * scroll: live it rendered 21 rows in a flat column, with hotkey badges on the
 * first eight and no way to narrow it. The filter appears only past the
 * threshold — under it, a search box on four options is noise.
 */
const MILESTONE_FILTER_THRESHOLD = 8;

function MilestoneWidget({
  projectId,
  onAnswer,
  ariaLabel,
  draftKey,
  current,
  suggestedOption,
}: {
  readonly projectId: number;
  readonly onAnswer: (text: string) => void;
  readonly ariaLabel?: string;
  readonly draftKey?: string;
  readonly current?: ChoiceCurrent;
  /** The milestone Loom's answer names — marked "Loom's suggestion". */
  readonly suggestedOption?: string;
}) {
  const t = useT();
  const armed = useHotkeysArmed();
  // `counts: false` — this picker renders the title plus a "closed"
  // sub-label and reads no count, and computing them costs the server a full
  // enumeration of every issue in the project (8–21s on a large one) while the
  // user looks at a bare loading line next to a durable Skip.
  const { data, loading, error, refetch } = useMilestones(projectId, {
    counts: false,
  });
  const all = data?.milestones ?? [];
  // Remount-safe like every other in-progress value on this surface: FocusFrame
  // rebuilds the widget subtree on each step-key change, and a half-typed filter
  // dying with it would re-drop the user into the unfiltered list of 21.
  const [filter, setFilter] = useRemountSafeState(
    draftKey !== undefined ? `${draftKey}:filter` : null,
    "",
  );
  const needle = filter.trim().toLowerCase();
  // Whether `title` is what the issue ALREADY carries (`current`, one of the
  // three provenances a choice step can mark — see `WidgetProps.current`).
  // Kept selectable even closed, or the one option that explains "keep this?"
  // would vanish from its own review question.
  const isCurrentSelection = (title: string): boolean =>
    current !== undefined &&
    (sameChoice(title, current.value) ||
      sameChoice(buildMilestoneAnswer(title), current.value));
  // R2-J: a CLOSED milestone is, with no filter typed, a live one-click
  // choice indistinguishable from an open one bar a small sub-label — live, 10
  // of 20 default options were closed and fully selectable, one click schedules
  // new work into a shipped container. Drop them from the default offer; the
  // search box (below) still finds one by name for the rare deliberate case,
  // since typing is an explicit act the default list rendering is not.
  const offered = all.filter(
    (r) => r.milestone.state !== "closed" || isCurrentSelection(r.milestone.title),
  );
  const rows =
    needle === ""
      ? offered
      : all.filter((r) => r.milestone.title.toLowerCase().includes(needle));
  // Which branch renders: the list, "Loading…", the error line, or the empty
  // line. NEVER a text box: the milestone is picked from the project's list or
  // skipped (the user's decision, 2026-10-02). The free-text fallback this
  // replaced let a typed name green, show on the ready screen and then vanish
  // at Accept (dev f7776b21, a repo with no milestones).
  //
  // `data === null` with no error is the 401 leg of `useMilestones` (it clears
  // `loading` and sets neither): that is an error, not "no milestones".
  //
  // The empty branch keys on what the list OFFERS with no filter typed — the
  // open milestones, plus the one the issue already carries — which is exactly
  // what the server's own check counts (`project-milestones.ts`, open only). A
  // project whose milestones are all closed is therefore "no open milestones"
  // here, as it is on the server once its check answers; before, it rendered
  // an empty list under "No milestone matches that." with no filter typed. Not
  // the FILTERED rows: a filter that matches nothing is `noMatch` below.
  const branch = loading
    ? "loading"
    : error !== null || data === null
      ? "error"
      : offered.length === 0
        ? "empty"
        : "dropdown";
  useEffect(() => {
    console.log(
      "[mile] widget.render " +
        JSON.stringify({
          branch,
          loading,
          error: error ? String(error).slice(0, 80) : null,
          rows: rows.length,
        }),
    );
  }, [branch, loading, error, rows.length]);
  // Every milestone answer goes through here: a pick sends exactly
  // `Milestone: <title>`, the shape the server stamps as a pick
  // (`milestonePickOf`) and grounds the milestone from.
  const emit = useCallback(
    (text: string): void => {
      console.log("[mile] widget.send " + JSON.stringify({ branch, text }));
      onAnswer(text);
    },
    [branch, onAnswer],
  );
  const pick = useCallback(
    (i: number) => {
      const row = rows[i];
      if (row) emit(buildMilestoneAnswer(row.milestone.title));
    },
    [rows, emit],
  );
  // Bound to the VISIBLE rows: with a filter live, A picks the first match, not
  // the first of the unfiltered twenty-one. That is what makes the badges honest
  // past the eighth option — narrowing is how you reach number nineteen.
  useHotkeySelect(armed ? Math.min(rows.length, HOTKEYS.length) : 0, pick);
  if (branch === "loading") {
    return <p className="focus-help">{t("focus.widget.milestone.loading")}</p>;
  }
  // No box in either state below. The step's own Skip (or Keep, on a concern
  // or the ready screen) sits outside this widget and is the way on.
  if (branch === "error") {
    return (
      <div className="focus-milestone-state" role="status">
        <p className="focus-help">{t("focus.widget.milestone.error")}</p>
        <GhostButton onClick={refetch}>{t("common.retry")}</GhostButton>
      </div>
    );
  }
  if (branch === "empty") {
    return (
      <p className="focus-help" role="status">
        {t("focus.widget.milestone.none")}
      </p>
    );
  }
  const selectedTitle =
    current === undefined
      ? null
      : (rows.find(
          ({ milestone: m }) =>
            sameChoice(m.title, current.value) ||
            sameChoice(buildMilestoneAnswer(m.title), current.value),
        )?.milestone.title ?? null);
  return (
    <>
      {all.length > MILESTONE_FILTER_THRESHOLD && (
        <input
          className="focus-field"
          type="search"
          aria-label={t("focus.widget.milestone.filterAria")}
          placeholder={t("focus.widget.milestone.filterPlaceholder")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      )}
      {/* OUTSIDE the radiogroup — a paragraph among radios is markup a screen
          reader has to guess at (same rule as the closed-option note). */}
      {rows.length === 0 && (
        <p className="focus-help">{t("focus.widget.milestone.noMatch")}</p>
      )}
      <div
        className="focus-options"
        role="radiogroup"
        aria-label={ariaLabel ?? t("focus.refine.choice.groupAria")}
      >
      {rows.map(({ milestone: m }, i) => {
        const selected = selectedTitle !== null && m.title === selectedTitle;
        // The previously-picked / carded milestone owns the sub line: on a list
        // of twenty otherwise identical rows it is the only thing that says what
        // an "Undo — go back" is undoing. `closed` still shows on every other
        // row, which is what it was always meant to do (the prop was being
        // dropped on the floor — JSX spreads skip excess-property checks).
        const suggested =
          suggestedOption !== undefined &&
          (sameChoice(m.title, suggestedOption) ||
            sameChoice(buildMilestoneAnswer(m.title), suggestedOption));
        const sub = selected && current !== undefined
          ? currentSubLabel(t, current.origin)
          : suggested
            ? t("focus.refine.choice.loomSuggests")
            : m.state === "closed"
              ? t("focus.refine.choice.closedMilestone")
              : null;
        return (
          <OptionButton
            key={m.id}
            label={m.title}
            selected={selected}
            {...(sub !== null ? { sub } : {})}
            {...(armed && HOTKEYS[i] ? { hotkey: HOTKEYS[i] } : {})}
            onClick={() => emit(buildMilestoneAnswer(m.title))}
          />
        );
      })}
      </div>
    </>
  );
}

/* ─── Dispatcher ─────────────────────────────────────────────────────── */

/** Render the purpose-built control for `field`. The control derives from the
 *  field (same discipline as ReadinessCard) — not the agent-sent widget, which
 *  is unreliable about option values. */
export function AnswerWidget({
  field,
  projectId,
  onAnswer,
  placeholder,
  ariaLabel,
  draftKey,
  initialValue,
  initialRows,
  loomAnswer,
  assentGuard,
  onKeep,
  refuseUnchanged,
  answerLabel,
  traceSurface,
  onClosedChange,
  current,
  autoFocus,
}: WidgetProps) {
  const t = useT();
  const widget = defaultWidgetForField(field);
  // The closed-answer props every free-text / list control shares.
  const shared = {
    ...(assentGuard !== undefined ? { assentGuard } : {}),
    ...(onKeep !== undefined ? { onKeep } : {}),
    ...(refuseUnchanged !== undefined ? { refuseUnchanged } : {}),
    ...(answerLabel !== undefined ? { answerLabel } : {}),
    ...(traceSurface !== undefined ? { traceSurface } : {}),
    ...(onClosedChange !== undefined ? { onClosedChange } : {}),
    ...(autoFocus !== undefined ? { autoFocus } : {}),
    loomWording: loomAnswer?.source === "unverified",
  };
  // A choice control already shows every option, so Loom's answer MARKS the one
  // it names rather than standing above them.
  const suggestedOption =
    loomAnswer !== undefined && loomAnswer.kind === "choice" ? loomAnswer.value : undefined;
  switch (widget.type) {
    case "single_select":
      return (
        <SelectWidget
          options={widget.options.length > 0 ? widget.options : PRIORITY_OPTIONS}
          onAnswer={onAnswer}
          {...(ariaLabel !== undefined ? { ariaLabel } : {})}
          {...(current !== undefined ? { current } : {})}
          {...(suggestedOption !== undefined ? { suggestedOption } : {})}
        />
      );
    case "dynamic_select":
      // Without a project there is no list to pick from, and a milestone is
      // never typed: say so, offer no box. (Every Focus surface passes a
      // project id; this is the guard, not a path.)
      return projectId === undefined ? (
        <p className="focus-help" role="status">
          {t("focus.widget.milestone.error")}
        </p>
      ) : (
        <MilestoneWidget
          projectId={projectId}
          onAnswer={onAnswer}
          {...(ariaLabel !== undefined ? { ariaLabel } : {})}
          {...(draftKey !== undefined ? { draftKey } : {})}
          {...(current !== undefined ? { current } : {})}
          {...(suggestedOption !== undefined ? { suggestedOption } : {})}
        />
      );
    case "list_builder":
      return (
        <ListWidget
          field={field}
          onAnswer={onAnswer}
          {...(placeholder !== undefined ? { placeholder } : {})}
          {...(ariaLabel !== undefined ? { ariaLabel } : {})}
          {...(draftKey !== undefined ? { draftKey } : {})}
          {...(initialRows !== undefined ? { initialRows } : {})}
          {...(loomAnswer !== undefined && loomAnswer.kind === "list"
            ? { loomRows: loomAnswer.rows }
            : {})}
          {...shared}
        />
      );
    case "text":
    default:
      return (
        <TextWidget
          field={field}
          onAnswer={onAnswer}
          {...(placeholder !== undefined ? { placeholder } : {})}
          {...(ariaLabel !== undefined ? { ariaLabel } : {})}
          {...(draftKey !== undefined ? { draftKey } : {})}
          {...(initialValue !== undefined ? { initialValue } : {})}
          {...(loomAnswer !== undefined && loomAnswer.kind === "text"
            ? { loomText: loomAnswer.text }
            : {})}
          {...shared}
        />
      );
  }
}
